"use client";

import React, { useState } from 'react';
import StarRating from './StarRating';
import { toast } from 'sonner';

interface WriteReviewFormProps {
  onSubmit: (review: {
    rating: number;
    title: string;
    content: string;
    /** Empty means they chose to stay anonymous. */
    authorName: string;
  }) => Promise<void>;
  onCancel: () => void;
}

const WriteReviewForm: React.FC<WriteReviewFormProps> = ({ onSubmit, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [authorName, setAuthorName] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    if (!content.trim()) {
      toast.error("Please enter your review");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit({
        rating,
        title: title.trim(),
        content: content.trim(),
        authorName: authorName.trim(),
      });
      
      // Reset form
      setRating(0);
      setAuthorName('');
      setTitle('');
      setContent('');
      
      toast.success("Review submitted successfully!");
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error("Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6 mb-8 animate-in fade-in slide-in-from-top-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-slate-900">Write your review</h3>
        <button 
          onClick={onCancel} 
          className="text-slate-400 hover:text-slate-600"
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Rating</label>
          <div className="flex justify-center p-4 bg-slate-50 rounded-xl border border-slate-100">
            <StarRating 
              rating={rating} 
              size={32} 
              onRatingChange={setRating} 
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Your name</label>
          <input
            type="text"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value.slice(0, 40))}
            placeholder="Leave blank to stay anonymous"
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-black outline-none"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
          <input 
            type="text" 
            required
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-black outline-none"
            placeholder="Summarize your experience..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-1">Review</label>
          <textarea 
            required
            rows={4}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-black outline-none resize-none"
            placeholder="Tell us more about the product..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <button 
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </button>
      </form>
    </div>
  );
};

export default WriteReviewForm;
